/**
 * CDM Dashboards — Track 2.6 Grafana-like layer over the CDM.
 *
 * A dashboard is a 12-column grid of tiles; each tile renders the rows
 * returned by its saved query, packaged per the tile's visualisation
 * (count / table / timeseries / pie / heatmap / graph). Foundation for
 * Track 2.7 (Enforcement Center bulk actions on query results).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Grid3x3, RefreshCw, Plus, Trash2, Edit3, X, BarChart3, Search,
  Eye, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts'
import { api } from '../lib/api'

interface Dashboard {
  id: string
  org_id: string
  name: string
  description: string | null
  layout: LayoutEntry[]
  visibility: string
  created_by: string
  created_at: string | null
  updated_at: string | null
}

interface LayoutEntry {
  tile_id: string
  x: number
  y: number
  w: number
  h: number
}

interface Tile {
  id: string
  org_id: string
  name: string
  query_id: string
  visualisation: 'count' | 'table' | 'timeseries' | 'pie' | 'heatmap' | 'graph'
  config: Record<string, unknown>
  created_by: string
  created_at: string | null
  updated_at: string | null
}

interface SavedQuery {
  id: string
  name: string
  display_name: string
  archetype: string
  dsl: Record<string, unknown>
  visibility: string
}

interface RenderedTile {
  tile: Tile
  rows?: Record<string, unknown>[]
  row_count?: number
  error?: string
}

const PIE_COLOURS = ['#06b6d4', '#a78bfa', '#10b981', '#f59e0b', '#f43f5e', '#3b82f6', '#ec4899', '#84cc16']

export default function CdmDashboardsPage() {
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [tiles, setTiles] = useState<Tile[]>([])
  const [queries, setQueries] = useState<SavedQuery[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [rendered, setRendered] = useState<Record<string, RenderedTile>>({})
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [drawerTile, setDrawerTile] = useState<RenderedTile | null>(null)
  const [showNewDash, setShowNewDash] = useState(false)
  const [showNewTile, setShowNewTile] = useState(false)
  const [message, setMessage] = useState('')

  const active = useMemo(
    () => dashboards.find(d => d.id === activeId) || null,
    [dashboards, activeId],
  )

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [dRes, tRes, qRes] = await Promise.all([
        api.get('/api/cdm/dashboards'),
        api.get('/api/cdm/tiles'),
        api.get('/api/cdm/queries'),
      ])
      const dList = (dRes.data?.dashboards || []) as Dashboard[]
      setDashboards(dList)
      setTiles((tRes.data?.tiles || []) as Tile[])
      setQueries((qRes.data?.queries || []) as SavedQuery[])
      if (dList.length && !activeId) setActiveId(dList[0].id)
    } finally {
      setLoading(false)
    }
  }, [activeId])

  useEffect(() => { void loadAll() }, [loadAll])

  // Render the active dashboard whenever it (or its tiles) change.
  useEffect(() => {
    if (!active) { setRendered({}); return }
    let alive = true
    ;(async () => {
      try {
        const r = await api.post(`/api/cdm/dashboards/${active.id}/render`)
        if (!alive) return
        setRendered((r.data?.tiles || {}) as Record<string, RenderedTile>)
      } catch {
        if (alive) setRendered({})
      }
    })()
    return () => { alive = false }
  }, [active])

  const tileById = useMemo(() => {
    const m: Record<string, Tile> = {}
    for (const t of tiles) m[t.id] = t
    return m
  }, [tiles])

  const queryById = useMemo(() => {
    const m: Record<string, SavedQuery> = {}
    for (const q of queries) m[q.id] = q
    return m
  }, [queries])

  // ── Mutations ──────────────────────────────────────────────────────
  const createDashboard = async (name: string) => {
    try {
      const r = await api.post('/api/cdm/dashboards', { name, layout: [], visibility: 'private' })
      const created = r.data as Dashboard
      setDashboards(prev => [created, ...prev])
      setActiveId(created.id)
      setShowNewDash(false)
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Create failed')
    }
  }

  const deleteDashboard = async () => {
    if (!active) return
    if (!confirm(`Delete dashboard "${active.name}"?`)) return
    try {
      await api.delete(`/api/cdm/dashboards/${active.id}`)
      setDashboards(prev => prev.filter(d => d.id !== active.id))
      setActiveId(dashboards.find(d => d.id !== active.id)?.id || null)
    } catch {
      setMessage('Delete failed — only the creator or an admin can delete')
    }
  }

  const addTileToLayout = async (tileId: string) => {
    if (!active) return
    // Find next free row slot — naive bottom-left packing
    const layout = [...(active.layout || [])]
    const maxY = layout.reduce((m, e) => Math.max(m, e.y + e.h), 0)
    layout.push({ tile_id: tileId, x: 0, y: maxY, w: 6, h: 4 })
    try {
      const r = await api.patch(`/api/cdm/dashboards/${active.id}`, { layout })
      const updated = r.data as Dashboard
      setDashboards(prev => prev.map(d => d.id === updated.id ? updated : d))
      setShowNewTile(false)
      // Re-render
      const rr = await api.post(`/api/cdm/dashboards/${active.id}/render`)
      setRendered((rr.data?.tiles || {}) as Record<string, RenderedTile>)
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Add tile failed')
    }
  }

  const removeFromLayout = async (tileId: string) => {
    if (!active) return
    const layout = (active.layout || []).filter(e => e.tile_id !== tileId)
    try {
      const r = await api.patch(`/api/cdm/dashboards/${active.id}`, { layout })
      setDashboards(prev => prev.map(d => d.id === r.data.id ? (r.data as Dashboard) : d))
    } catch {
      setMessage('Remove failed')
    }
  }

  const moveTile = async (tileId: string, dx: number, dy: number) => {
    if (!active) return
    const layout = (active.layout || []).map(e => e.tile_id === tileId
      ? { ...e, x: Math.max(0, Math.min(12 - e.w, e.x + dx)), y: Math.max(0, e.y + dy) }
      : e)
    try {
      const r = await api.patch(`/api/cdm/dashboards/${active.id}`, { layout })
      setDashboards(prev => prev.map(d => d.id === r.data.id ? (r.data as Dashboard) : d))
    } catch { /* ignore */ }
  }

  const resizeTile = async (tileId: string, dw: number, dh: number) => {
    if (!active) return
    const layout = (active.layout || []).map(e => e.tile_id === tileId
      ? { ...e, w: Math.max(2, Math.min(12 - e.x, e.w + dw)), h: Math.max(2, e.h + dh) }
      : e)
    try {
      const r = await api.patch(`/api/cdm/dashboards/${active.id}`, { layout })
      setDashboards(prev => prev.map(d => d.id === r.data.id ? (r.data as Dashboard) : d))
    } catch { /* ignore */ }
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-3 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Grid3x3 size={18} className="text-cyan" /> CDM DASHBOARDS
        </h1>
        <select
          value={activeId || ''}
          onChange={e => setActiveId(e.target.value || null)}
          className="bg-surface-card border border-border rounded px-2 py-1 text-xs text-text-primary"
        >
          <option value="">— pick a dashboard —</option>
          {dashboards.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <button type="button" onClick={() => setShowNewDash(true)} className="btn-ghost">
          <Plus size={11} /> New dashboard
        </button>
        {active && (
          <>
            <button
              type="button"
              onClick={() => setEditing(v => !v)}
              className={clsx('btn-ghost', editing && 'text-emerald')}
            >
              <Edit3 size={11} /> {editing ? 'Done editing' : 'Edit layout'}
            </button>
            <button type="button" onClick={() => setShowNewTile(true)} className="btn-ghost">
              <Plus size={11} /> Add tile
            </button>
            <button type="button" onClick={() => void deleteDashboard()} className="btn-ghost text-rose-light">
              <Trash2 size={11} /> Delete
            </button>
          </>
        )}
        <Link to="/app/cdm/queries" className="btn-ghost ml-auto">
          <Search size={11} /> Saved-query library
        </Link>
        <button type="button" onClick={() => void loadAll()} disabled={loading} className="btn-ghost">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {message && (
        <div className="card p-2 text-[11px] text-text-secondary">
          {message}
          <button type="button" onClick={() => setMessage('')} className="float-right">
            <X size={11} />
          </button>
        </div>
      )}

      {!active && (
        <div className="card p-6 text-center text-text-muted text-sm">
          No dashboard selected. Pick one from the dropdown or create a new one.
        </div>
      )}

      {active && (
        <div className="space-y-2">
          <div className="text-[11px] text-text-muted">
            {active.description || 'No description.'} · {active.layout?.length || 0} tiles · created by {active.created_by}
          </div>

          <div className="grid grid-cols-12 gap-3">
            {(active.layout || []).map(entry => {
              const tile = tileById[entry.tile_id]
              const r = rendered[entry.tile_id]
              return (
                <div
                  key={entry.tile_id}
                  className="card p-3 space-y-2"
                  style={{
                    gridColumn: `span ${Math.min(12, Math.max(2, entry.w))} / span ${Math.min(12, Math.max(2, entry.w))}`,
                    minHeight: `${Math.max(120, entry.h * 60)}px`,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 size={12} className="text-cyan" />
                    <h2 className="text-xs font-bold text-text-primary truncate">
                      {tile?.name || '(missing tile)'}
                    </h2>
                    <span className="text-[9px] text-text-muted ml-auto">
                      {tile?.visualisation || '?'}
                      {typeof r?.row_count === 'number' && ` · ${r.row_count} rows`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setDrawerTile(r ?? { tile: tile! })}
                      className="text-text-muted hover:text-text-primary"
                      aria-label="View tile detail"
                    >
                      <Eye size={11} />
                    </button>
                    {editing && (
                      <>
                        <button type="button" onClick={() => void moveTile(entry.tile_id, -1, 0)} className="text-text-muted hover:text-text-primary"><ChevronLeft size={11} /></button>
                        <button type="button" onClick={() => void moveTile(entry.tile_id, 1, 0)} className="text-text-muted hover:text-text-primary"><ChevronRight size={11} /></button>
                        <button type="button" onClick={() => void resizeTile(entry.tile_id, 1, 0)} className="text-text-muted hover:text-text-primary text-[9px]">w+</button>
                        <button type="button" onClick={() => void resizeTile(entry.tile_id, -1, 0)} className="text-text-muted hover:text-text-primary text-[9px]">w-</button>
                        <button type="button" onClick={() => void resizeTile(entry.tile_id, 0, 1)} className="text-text-muted hover:text-text-primary text-[9px]">h+</button>
                        <button type="button" onClick={() => void resizeTile(entry.tile_id, 0, -1)} className="text-text-muted hover:text-text-primary text-[9px]">h-</button>
                        <button type="button" onClick={() => void removeFromLayout(entry.tile_id)} className="text-rose-light hover:text-rose"><Trash2 size={11} /></button>
                      </>
                    )}
                  </div>
                  {tile ? <TileBody tile={tile} render={r} /> : null}
                </div>
              )
            })}
            {(!active.layout || active.layout.length === 0) && (
              <div className="col-span-12 card p-6 text-center text-text-muted text-sm">
                No tiles yet. Click "Add tile" to drop one onto the grid.
              </div>
            )}
          </div>
        </div>
      )}

      {/* New-dashboard dialog */}
      {showNewDash && (
        <NewDashboardDialog onClose={() => setShowNewDash(false)} onCreate={createDashboard} />
      )}

      {/* Add-tile dialog */}
      {showNewTile && active && (
        <AddTileDialog
          tiles={tiles}
          existing={new Set(active.layout?.map(e => e.tile_id))}
          onClose={() => setShowNewTile(false)}
          onPick={addTileToLayout}
          onCreated={async (newTile) => {
            setTiles(prev => [newTile, ...prev])
            await addTileToLayout(newTile.id)
          }}
          queries={queries}
        />
      )}

      {/* Drawer */}
      {drawerTile && (
        <TileDrawer
          tile={drawerTile}
          query={drawerTile.tile ? queryById[drawerTile.tile.query_id] : undefined}
          onClose={() => setDrawerTile(null)}
        />
      )}
    </div>
  )
}


// ── Tile body renderer per visualisation ───────────────────────────

function TileBody({ tile, render }: { tile: Tile; render?: RenderedTile }) {
  if (!render) {
    return <div className="text-text-muted text-[11px]">Loading…</div>
  }
  if (render.error) {
    return <div className="text-rose-light text-[11px]">{render.error}</div>
  }
  const rows = render.rows || []
  const cfg = (tile.config || {}) as Record<string, any>

  if (tile.visualisation === 'count') {
    const value = rows.length === 1 && rows[0]?.value !== undefined ? rows[0].value : rows.length
    return (
      <div className="text-3xl font-bold text-cyan">{String(value)}</div>
    )
  }

  if (tile.visualisation === 'table') {
    const cols = rows.length ? Object.keys(rows[0]) : []
    return (
      <div className="overflow-auto max-h-64">
        <table className="text-[10px] w-full">
          <thead>
            <tr className="border-b border-border">
              {cols.map(c => <th key={c} className="text-left p-1 text-text-muted">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-b border-border/40">
                {cols.map(c => (
                  <td key={c} className="p-1 truncate text-text-secondary" style={{ maxWidth: 200 }}>
                    {fmtCell(r[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-text-muted text-[10px] p-2">No rows.</div>}
      </div>
    )
  }

  if (tile.visualisation === 'timeseries') {
    const x = (cfg.x as string) || 'observed_at'
    const y = (cfg.y as string) || 'value'
    const data = rows.map(r => ({ x: r[x], y: Number(r[y]) || 0 }))
    return (
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid stroke="#26262c" strokeDasharray="3 3" />
            <XAxis dataKey="x" tick={{ fontSize: 9, fill: '#888' }} />
            <YAxis tick={{ fontSize: 9, fill: '#888' }} />
            <Tooltip contentStyle={{ background: '#18181c', border: '1px solid #26262c', fontSize: 11 }} />
            <Line type="monotone" dataKey="y" stroke="#06b6d4" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )
  }

  if (tile.visualisation === 'pie') {
    const slice = (cfg.slice_field as string) || (rows[0] ? Object.keys(rows[0]).find(k => k !== 'value') : null) || 'name'
    const data = rows.map(r => ({ name: String(r[slice]), value: Number(r.value) || 0 }))
    return (
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" outerRadius={60} label={{ fontSize: 10 }}>
              {data.map((_, i) => <Cell key={i} fill={PIE_COLOURS[i % PIE_COLOURS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: '#18181c', border: '1px solid #26262c', fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    )
  }

  if (tile.visualisation === 'heatmap') {
    // Simple bar chart fallback — proper heatmap can come later.
    const data = rows.slice(0, 30).map(r => ({
      label: `${r.x ?? ''}/${r.y ?? ''}`,
      value: Number(r.value) || 0,
    }))
    return (
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={data}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#888' }} />
            <YAxis tick={{ fontSize: 9, fill: '#888' }} />
            <Tooltip contentStyle={{ background: '#18181c', border: '1px solid #26262c', fontSize: 11 }} />
            <Bar dataKey="value" fill="#a78bfa" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  }

  if (tile.visualisation === 'graph') {
    return (
      <div className="text-[10px] text-text-muted">
        Graph rendering not yet wired — {rows.length} rows ready. Use the
        drawer's "View raw rows" to inspect.
      </div>
    )
  }

  return <div className="text-text-muted text-[11px]">Unknown visualisation: {tile.visualisation}</div>
}


// ── Helpers ────────────────────────────────────────────────────────

function fmtCell(v: any): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}


// ── Sub-dialogs ────────────────────────────────────────────────────

function NewDashboardDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string) => Promise<void> }) {
  const [name, setName] = useState('')
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="card p-4 max-w-sm w-full space-y-3">
        <div className="flex items-center gap-2">
          <Grid3x3 size={14} className="text-cyan" />
          <h2 className="text-sm font-bold text-text-primary">New dashboard</h2>
          <button type="button" onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary"><X size={12} /></button>
        </div>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Name (e.g. Banking Operations)"
          className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            type="button"
            disabled={!name.trim()}
            onClick={() => void onCreate(name.trim())}
            className="btn-primary"
          >
            <Plus size={11} /> Create
          </button>
        </div>
      </div>
    </div>
  )
}


function AddTileDialog({
  tiles, existing, queries, onClose, onPick, onCreated,
}: {
  tiles: Tile[]
  existing: Set<string>
  queries: SavedQuery[]
  onClose: () => void
  onPick: (tileId: string) => void
  onCreated: (tile: Tile) => void
}) {
  const [tab, setTab] = useState<'pick' | 'new'>('pick')
  const [name, setName] = useState('')
  const [queryId, setQueryId] = useState('')
  const [vis, setVis] = useState<Tile['visualisation']>('count')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const create = async () => {
    setCreating(true)
    setError('')
    try {
      const r = await api.post('/api/cdm/tiles', { name: name.trim(), query_id: queryId, visualisation: vis, config: {} })
      onCreated(r.data as Tile)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Create failed')
    } finally { setCreating(false) }
  }

  const available = tiles.filter(t => !existing.has(t.id))

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="card p-4 max-w-md w-full space-y-3">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-cyan" />
          <h2 className="text-sm font-bold text-text-primary">Add tile</h2>
          <button type="button" onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary"><X size={12} /></button>
        </div>
        <div className="flex gap-2 text-xs">
          <button type="button" onClick={() => setTab('pick')} className={clsx('btn-ghost', tab === 'pick' && 'text-emerald')}>Pick existing</button>
          <button type="button" onClick={() => setTab('new')} className={clsx('btn-ghost', tab === 'new' && 'text-emerald')}>Create new</button>
        </div>

        {tab === 'pick' && (
          <div className="space-y-1 max-h-64 overflow-auto">
            {available.length === 0 && (
              <div className="text-text-muted text-[11px]">All tiles already on the dashboard. Create a new one.</div>
            )}
            {available.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => onPick(t.id)}
                className="w-full text-left p-2 rounded border border-border hover:border-cyan/40 text-xs"
              >
                <div className="font-bold text-text-primary">{t.name}</div>
                <div className="text-text-muted text-[10px]">{t.visualisation}</div>
              </button>
            ))}
          </div>
        )}

        {tab === 'new' && (
          <div className="space-y-2 text-xs">
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Tile name"
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            />
            <select
              value={queryId}
              onChange={e => setQueryId(e.target.value)}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            >
              <option value="">— pick a saved query —</option>
              {queries.map(q => (
                <option key={q.id} value={q.id}>{q.display_name} ({q.archetype})</option>
              ))}
            </select>
            <select
              value={vis}
              onChange={e => setVis(e.target.value as Tile['visualisation'])}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            >
              <option value="count">count</option>
              <option value="table">table</option>
              <option value="timeseries">timeseries</option>
              <option value="pie">pie</option>
              <option value="heatmap">heatmap</option>
              <option value="graph">graph</option>
            </select>
            {error && <div className="text-rose-light text-[10px]">{error}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
              <button
                type="button"
                disabled={!name.trim() || !queryId || creating}
                onClick={() => void create()}
                className="btn-primary"
              >
                <Plus size={11} /> {creating ? 'Creating…' : 'Create + add'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


function TileDrawer({
  tile, query, onClose,
}: {
  tile: RenderedTile
  query?: SavedQuery
  onClose: () => void
}) {
  const [showRaw, setShowRaw] = useState(false)
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-surface-card border-l border-border z-50 overflow-auto p-4 space-y-3">
      <div className="flex items-center gap-2">
        <BarChart3 size={14} className="text-cyan" />
        <h2 className="text-sm font-bold text-text-primary">{tile.tile?.name}</h2>
        <button type="button" onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary"><X size={12} /></button>
      </div>
      <div className="text-[11px] text-text-muted">
        Visualisation: {tile.tile?.visualisation} · Rows: {tile.row_count ?? 0}
      </div>
      {tile.error && <div className="text-rose-light text-[11px]">{tile.error}</div>}
      <div>
        <div className="text-[10px] text-text-muted">Saved query</div>
        <div className="text-xs text-text-primary">{query?.display_name || query?.name || tile.tile?.query_id}</div>
      </div>
      <div>
        <div className="text-[10px] text-text-muted">Query DSL</div>
        <pre className="text-[10px] font-mono whitespace-pre-wrap break-all bg-black/30 p-2 rounded">
          {JSON.stringify(query?.dsl ?? {}, null, 2)}
        </pre>
      </div>
      <button type="button" onClick={() => setShowRaw(v => !v)} className="btn-ghost">
        <Eye size={11} /> {showRaw ? 'Hide raw rows' : 'View raw rows'}
      </button>
      {showRaw && (
        <pre className="text-[10px] font-mono whitespace-pre-wrap break-all bg-black/30 p-2 rounded max-h-96 overflow-auto">
          {JSON.stringify(tile.rows ?? [], null, 2)}
        </pre>
      )}
    </div>
  )
}
